import { z } from 'zod';

// src/precedence/enums.generated.ts
var CatalogScope = {
  ENTITY: "entity",
  ADDRESS: "address",
  TRANSACTION: "transaction",
  EVENT: "event",
  INTERNAL_TX: "internal_tx"
};
var CatalogLifecycle = {
  VERIFIED: "verified",
  CANDIDATE: "candidate",
  DEPRECATED: "deprecated",
  QUARANTINED: "quarantined"
};
var MemberTier = {
  CORE: "core",
  DERIVED: "derived"
};
var CatalogKind = {
  TAG: "tag",
  LABEL: "label",
  GROUP: "group",
  ENTITY: "entity"
};
var Severity = {
  INFO: "info",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical"
};
var RiskLevel = {
  SAFE: "safe",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  SEVERE: "severe"
};

// src/precedence/tier.ts
var Tier = {
  USER_OVERRIDE: "user_override",
  USER_BASE: "user_base",
  /** Tenant scope, verified (wire value kept as `customer_override`). */
  CUSTOMER_OVERRIDE: "customer_override",
  /** Tenant scope, unverified (wire value kept as `customer_base`). */
  CUSTOMER_BASE: "customer_base",
  /** Org-wide scope, verified — below tenant, above default. */
  ORG_OVERRIDE: "org_override",
  /** Org-wide scope, unverified. */
  ORG_BASE: "org_base",
  DEFAULT: "default",
  ENTITY_INHERITED: "entity_inherited",
  PROVIDER_CANDIDATE: "provider_candidate"
};
var TENANT_OVERRIDE = Tier.CUSTOMER_OVERRIDE;
var TENANT_BASE = Tier.CUSTOMER_BASE;
var TIER_WEIGHTS = {
  [Tier.USER_OVERRIDE]: 70,
  [Tier.USER_BASE]: 60,
  [Tier.CUSTOMER_OVERRIDE]: 50,
  [Tier.CUSTOMER_BASE]: 40,
  // FE-only, in the gap: below tenant, above the system default. A wire weight
  // is never 36/33, because the backend has no org tier to emit one.
  [Tier.ORG_OVERRIDE]: 36,
  [Tier.ORG_BASE]: 33,
  [Tier.DEFAULT]: 30,
  [Tier.ENTITY_INHERITED]: 20,
  [Tier.PROVIDER_CANDIDATE]: 10
};
function tierWeight(tier) {
  return TIER_WEIGHTS[tier] ?? TIER_WEIGHTS[Tier.PROVIDER_CANDIDATE];
}
function compareTiers(a, b) {
  const wa = tierWeight(a);
  const wb = tierWeight(b);
  if (wa < wb) return -1;
  if (wa > wb) return 1;
  return 0;
}
function deriveTier(input) {
  const { hasCustomerScope, isVerified, inheritedFromEntity } = input;
  const isUserScoped = input.isUserScoped ?? false;
  const isOrgScoped = input.isOrgScoped ?? false;
  if (isUserScoped && isVerified) return Tier.USER_OVERRIDE;
  if (isUserScoped) return Tier.USER_BASE;
  if (hasCustomerScope && isVerified) return Tier.CUSTOMER_OVERRIDE;
  if (hasCustomerScope) return Tier.CUSTOMER_BASE;
  if (isOrgScoped && isVerified) return Tier.ORG_OVERRIDE;
  if (isOrgScoped) return Tier.ORG_BASE;
  if (inheritedFromEntity) return Tier.ENTITY_INHERITED;
  if (isVerified) return Tier.DEFAULT;
  return Tier.PROVIDER_CANDIDATE;
}
function mergeByPrecedence(hits, keyFn, tierFn) {
  const chosen = /* @__PURE__ */ new Map();
  for (const hit of hits) {
    const k = keyFn(hit);
    const existing = chosen.get(k);
    if (existing === void 0 || tierWeight(tierFn(hit)) > tierWeight(tierFn(existing))) {
      chosen.set(k, hit);
    }
  }
  return [...chosen.values()];
}
function resolve(hits, opts) {
  const { keyFn, deriveTierFn } = opts;
  const tenantContext = opts.tenantContext ?? null;
  const chainId = opts.chainId ?? null;
  return mergeByPrecedence(hits, keyFn, (h) => deriveTierFn(h, tenantContext, chainId));
}

// src/precedence/overrides.ts
function isSuppressed(lifecycle) {
  return lifecycle === CatalogLifecycle.QUARANTINED;
}
function filterSuppressed(hits, lifecycleFn) {
  return [...hits].filter((hit) => !isSuppressed(lifecycleFn(hit)));
}
function applyOverrides(base, overrides, opts) {
  const all = [...base, ...overrides];
  const lifecycleFn = opts.lifecycleFn;
  const winners = mergeByPrecedence(all, opts.keyFn, opts.tierFn);
  if (!lifecycleFn) return winners;
  return winners.filter((hit) => !isSuppressed(lifecycleFn(hit)));
}

// src/precedence/risk-ladder.generated.json
var risk_ladder_generated_default = {
  bands: {
    high: 60,
    low: 20,
    medium: 40,
    severe: 85
  },
  level_by_severity: {
    critical: "severe",
    high: "high",
    low: "low",
    medium: "medium"
  },
  scale_top: 100,
  score_by_level: {
    high: 72.5,
    low: 30,
    medium: 50,
    safe: 10,
    severe: 92.5
  },
  score_by_severity: {
    critical: 92.5,
    high: 72.5,
    low: 30,
    medium: 50
  },
  severity_by_level: {
    high: "high",
    low: "low",
    medium: "medium",
    severe: "critical"
  }
};

// src/precedence/risk-ladder.ts
var LadderSchema = z.object({
  scale_top: z.number(),
  bands: z.object({
    severe: z.number(),
    high: z.number(),
    medium: z.number(),
    low: z.number()
  }),
  level_by_severity: z.record(z.string(), z.string()),
  severity_by_level: z.record(z.string(), z.string()),
  score_by_level: z.record(z.string(), z.number()),
  score_by_severity: z.record(z.string(), z.number())
});
var SNAPSHOT = LadderSchema.parse(risk_ladder_generated_default);
var RISK_SCORE_SCALE_TOP = SNAPSHOT.scale_top;
var RISK_SCORE_BANDS = Object.freeze({ ...SNAPSHOT.bands });
var LEVEL_BY_SEVERITY = Object.freeze(
  SNAPSHOT.level_by_severity
);
var SEVERITY_BY_LEVEL = Object.freeze(
  SNAPSHOT.severity_by_level
);
var SCORE_BY_SEVERITY = Object.freeze(
  SNAPSHOT.score_by_severity
);
var SCORE_BY_LEVEL = Object.freeze(SNAPSHOT.score_by_level);

// src/precedence/risk.ts
var RISK_LEVEL_ORDER = [
  RiskLevel.SAFE,
  RiskLevel.LOW,
  RiskLevel.MEDIUM,
  RiskLevel.HIGH,
  RiskLevel.SEVERE
];
var RISK_LEVEL_RANK = Object.freeze(
  Object.fromEntries(RISK_LEVEL_ORDER.map((level, i) => [level, i]))
);
var RISK_VALUES = new Set(RISK_LEVEL_ORDER);
function coerceRisk(value) {
  const s = typeof value === "string" ? value.toLowerCase() : "";
  return RISK_VALUES.has(s) ? s : void 0;
}
function worstBy(items, map, levelOf) {
  let worst;
  let worstRank = -1;
  for (const item of items) {
    const mapped = map(item);
    if (mapped === void 0) continue;
    const rank = RISK_LEVEL_RANK[levelOf(mapped)] ?? 0;
    if (rank > worstRank) {
      worst = mapped;
      worstRank = rank;
    }
  }
  return worst;
}
function worstRisk(values) {
  return worstBy(values, coerceRisk, (level) => level);
}
var DEFAULT_RISK_SCORE_BANDS = RISK_SCORE_BANDS;
function riskLevelFromScore(score, bands = DEFAULT_RISK_SCORE_BANDS) {
  if (score == null || !Number.isFinite(score)) return void 0;
  if (score >= bands.severe) return RiskLevel.SEVERE;
  if (score >= bands.high) return RiskLevel.HIGH;
  if (score >= bands.medium) return RiskLevel.MEDIUM;
  if (score >= bands.low) return RiskLevel.LOW;
  return RiskLevel.SAFE;
}

// src/precedence/risk-rules.generated.json
var risk_rules_generated_default = {
  policies: [
    {
      description: "A counterparty is a sanctioned entity or carries a sanctions tag.",
      enabled: true,
      id: "risk:ofac_sanctions",
      label: "OFAC sanctions exposure",
      max_score: 100
    },
    {
      description: "A counterparty is a mixing service, or the transaction itself is a mixing round.",
      enabled: true,
      id: "risk:mixer_exposure",
      label: "Mixer / tumbler exposure",
      max_score: 80
    },
    {
      description: "The sender carries the behavioural dust-spammer tag.",
      enabled: true,
      id: "risk:dust_spammer",
      label: "Dust spammer counterparty",
      max_score: 30
    },
    {
      description: "The sender is publicly tagged as an address poisoner.",
      enabled: true,
      id: "risk:address_poisoning",
      label: "Address poisoning",
      max_score: 75
    },
    {
      description: "The transaction moves a token that claims to be a well-known asset it is not.",
      enabled: true,
      id: "risk:counterfeit_token",
      label: "Counterfeit token",
      max_score: 75
    },
    {
      description: "The transaction moved a large USD value.",
      enabled: true,
      id: "risk:high_value",
      label: "High-value transaction",
      max_score: 60
    }
  ],
  signals: [
    {
      description: "A counterparty belongs to an entity flagged as sanctioned (e.g. the OFAC SDN list).",
      enabled: true,
      id: "ofac:sanctioned_entity",
      label: "Sanctioned entity",
      policy_id: "risk:ofac_sanctions",
      severity: "critical",
      weight: 100
    },
    {
      description: "A counterparty carries a sanctions tag.",
      enabled: true,
      id: "ofac:sanctioned_tag",
      label: "Sanctions tag",
      policy_id: "risk:ofac_sanctions",
      severity: "critical",
      weight: 100
    },
    {
      description: "A counterparty is tagged as a mixing service (Tornado Cash, a tumbler).",
      enabled: true,
      id: "mixer:tagged_address",
      label: "Mixer-tagged address",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "A counterparty belongs to an entity categorised as a mixer or tumbler.",
      enabled: true,
      id: "mixer:entity_match",
      label: "Mixer entity",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "The transaction itself is a mixing protocol whose implementation is identified \u2014 a Wasabi, Whirlpool or JoinMarket round, a Tornado deposit.",
      enabled: true,
      id: "mixer:protocol",
      label: "Named mixing protocol",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "The transaction has a CoinJoin's structure but no known implementation built it. Scored below a named mixer: the behaviour is proven, the service is not.",
      enabled: true,
      id: "mixer:structure",
      label: "CoinJoin structure, tool unidentified",
      policy_id: "risk:mixer_exposure",
      severity: "medium",
      weight: 50
    },
    {
      description: "The sender carries the behavioural dust-spammer tag.",
      enabled: true,
      id: "dust:spammer_sender",
      label: "Dust-spammer sender",
      policy_id: "risk:dust_spammer",
      severity: "low",
      weight: 25
    },
    {
      description: "The sender is publicly tagged as an address poisoner.",
      enabled: true,
      id: "poisoning:tagged_sender",
      label: "Address-poisoner sender",
      policy_id: "risk:address_poisoning",
      severity: "high",
      weight: 75
    },
    {
      description: "A token in the transaction calls itself by a registered asset's symbol or name (USDT, USDC, ETH) at an address that is not that asset's on this chain.",
      enabled: true,
      id: "counterfeit:token_claim",
      label: "Counterfeit token",
      policy_id: "risk:counterfeit_token",
      severity: "high",
      weight: 75
    },
    {
      description: "The transaction moved at least $1,000,000.",
      enabled: true,
      id: "high_value:gte_1m",
      label: "At least $1M moved",
      policy_id: "risk:high_value",
      severity: "high",
      weight: 60
    },
    {
      description: "The transaction moved at least $100,000.",
      enabled: true,
      id: "high_value:gte_100k",
      label: "At least $100k moved",
      policy_id: "risk:high_value",
      severity: "medium",
      weight: 40
    }
  ]
};

// src/precedence/risk-rules.ts
var SIGNALS = new Map(
  risk_rules_generated_default.signals.map((s) => [s.id, s])
);
var POLICIES = new Map(
  risk_rules_generated_default.policies.map((p) => [p.id, p])
);
function riskSignalRule(signalId) {
  return SIGNALS.get(signalId);
}
function riskPolicyRule(policyId) {
  return POLICIES.get(policyId);
}
function riskSignalSettingPath(signalId) {
  return `risk_signals.${signalId}.weight`;
}

export { CatalogKind, CatalogLifecycle, CatalogScope, DEFAULT_RISK_SCORE_BANDS, LEVEL_BY_SEVERITY, MemberTier, RISK_LEVEL_ORDER, RISK_LEVEL_RANK, RISK_SCORE_BANDS, RISK_SCORE_SCALE_TOP, RiskLevel, SCORE_BY_LEVEL, SCORE_BY_SEVERITY, SEVERITY_BY_LEVEL, Severity, TENANT_BASE, TENANT_OVERRIDE, TIER_WEIGHTS, Tier, applyOverrides, coerceRisk, compareTiers, deriveTier, filterSuppressed, isSuppressed, mergeByPrecedence, resolve, riskLevelFromScore, riskPolicyRule, riskSignalRule, riskSignalSettingPath, tierWeight, worstBy, worstRisk };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map